<template>
  <CCol xs="12" class="mt-2">
    <CFormLabel>
      {{ $t('api.public.users.sign_up_certified_partner.energy_manager_serialnos.label', { apxLabel }) }}
    </CFormLabel>
    <CInputGroup>
      <CFormInput
        id="input-serialno"
        v-model="newSerialno"
        placeholder="XXXH123456789"
        :invalid="v$.newSerialno.$error || v$.serialnos.$error"
        @change="atChange"
        @keyup.enter="addSerialno"
      />
      <CButton class="rounded-end" type="button" color="primary" @click="addSerialno">
        <CIcon icon="cil-plus" size="xl" />
      </CButton>
      <span class="invalid-feedback">{{ invalidFeedback }}</span>
    </CInputGroup>

    <CListGroup v-if="modelValue.length" class="mt-3">
      <CListGroupItem
        v-for="sno in modelValue"
        :key="sno"
        class="d-flex justify-content-between align-items-center p-2"
      >
        <span class="ms-2 fs-5">
          {{ sno }}
        </span>
        <CButton type="button" color="danger" size="sm" @click="removeSerialno(sno)">
          <CIcon icon="cil-trash" size="lg" />
        </CButton>
      </CListGroupItem>
    </CListGroup>
  </CCol>
</template>

<script>
import { computed, ref } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { serialnoValidator } from '@/validations/generic/amperix_validators'
import { useI18n } from 'vue-i18n'

export default {
  name: 'CertifiedPartnerSerialnosForm',
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    hideVueValidation: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'validation-change'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const apxLabel = document.getElementById('main')?.getAttribute('data-apx-label') || 'Energy Manager'

    const newSerialno = ref('')
    const hasInteracted = ref(false)
    const serialnos = computed(() => props.modelValue)

    const normalizedNewSerialno = computed(() => newSerialno.value.toUpperCase())
    const hasSerialnos = (value) => value.length > 0
    const hasSerialnosValidator = helpers.withParams({ type: 'hasSerialnos' }, hasSerialnos)

    const rules = computed(() => ({
      newSerialno: {
        validSerialnoSyntax: (value) => (value ? serialnoValidator.serialno.validSerialnoSyntax(value) : true),
        uniqueSerialno: helpers.withParams({ type: 'uniqueSerialno' }, (value) =>
          value ? !props.modelValue.includes(value.toUpperCase()) : true
        )
      },
      serialnos: {
        hasSerialnos: hasSerialnosValidator
      }
    }))

    const v$ = useVuelidate(rules, { newSerialno, serialnos })

    const newSerialnoIsValid = computed(() => !v$.value.newSerialno.$invalid)
    const serialnosAreValid = computed(() => !v$.value.serialnos.$invalid)

    const invalidFeedback = computed(() => {
      if (!newSerialnoIsValid.value)
        return t('api.public.users.sign_up_certified_partner.energy_manager_serialnos.invalid.serialno')

      if (!serialnosAreValid.value)
        return t('api.public.users.sign_up_certified_partner.energy_manager_serialnos.invalid.serialnos', {
          apxLabel
        })

      return ''
    })

    const emitValidationChange = ({
      modelValue = props.modelValue,
      isNewSerialnoValid = newSerialnoIsValid.value
    } = {}) => {
      emit('validation-change', {
        hasSerialnos: hasSerialnos(modelValue),
        isNewSerialnoValid,
        hasInteracted: hasInteracted.value
      })
    }

    emitValidationChange()

    return {
      addSerialno() {
        hasInteracted.value = true
        v$.value.newSerialno.$touch()

        if (!newSerialno.value) {
          emitValidationChange()
          return
        }

        newSerialno.value = normalizedNewSerialno.value

        if (v$.value.newSerialno.$invalid) {
          emitValidationChange()
          return
        }

        const nextSerialnos = [...props.modelValue, newSerialno.value].sort()
        emit('update:modelValue', nextSerialnos)
        newSerialno.value = ''
        v$.value.newSerialno.$reset()
        emitValidationChange({ modelValue: nextSerialnos, isNewSerialnoValid: true })
      },
      atChange: () => {
        hasInteracted.value = true
        v$.value.newSerialno.$touch()
        emitValidationChange()
      },
      apxLabel,
      invalidFeedback,
      newSerialno,
      removeSerialno(serialno) {
        hasInteracted.value = true
        const nextSerialnos = props.modelValue.filter((sn) => sn !== serialno)
        emit('update:modelValue', nextSerialnos)
        emitValidationChange({ modelValue: nextSerialnos })
      },
      v$
    }
  }
}
</script>
